import * as React from "react";

import * as Base from "DistributedTaskControls/Common/Components/Base";
import { ComboBoxType } from "DistributedTaskControls/SharedControls/InputControls/Components/ComboBoxInputComponent";
import { FlatViewDropdown } from "DistributedTaskControls/Components/FlatViewDropdown";
import { FlatViewIcon } from "DistributedTaskControls/Components/FlatViewIcon";
import { FlatViewTable } from "DistributedTaskControls/Components/FlatViewTable";
import { FlatViewText } from "DistributedTaskControls/Components/FlatViewText";
import { IFlatViewTableRow, ContentType, IFlatViewCell, IFlatViewColumn, ICellIndex } from "DistributedTaskControls/Common/FlatViewTableTypes";
import { TooltipIfOverflow } from "DistributedTaskControls/Components/TooltipIfOverflow";
import { ContributionSource } from "DistributedTaskControls/Sources/ContributionSource";
import { DirectionalHint, TooltipHost } from "VSSUI/Tooltip";
import { ContextualMenuButton } from "VSSUI/ContextualMenuButton";
import { ArtifactUtility } from "PipelineWorkflow/Scripts/Common/ArtifactUtility";
import { CreateReleaseArtifactConstants } from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/Constants";
import { FeatureFlagUtils } from "PipelineWorkflow/Scripts/Shared/Utils/FeatureFlagUtils";
import {
    IArtifactVersion,
    IVersionPickerMenuItemContext,
    PipelineArtifactDefinition,
    PipelineArtifactDefinitionConstants,
    PipelineArtifactVersion,
    PipelineBuildVersion
} from "PipelineWorkflow/Scripts/Common/Types";
import * as Resources from "PipelineWorkflow/Scripts/Resources/TFS.Resources.PipelineWorkflow";
import * as Store from "PipelineWorkflow/Scripts/SharedComponents/CreateRelease/CreateReleaseStore";
import * as EnvironmentUtils from "PipelineWorkflow/Scripts/Shared/Utils/ReleaseEnvironmentUtils";
import { ArtifactTypes } from "ReleasePipeline/Scripts/TFS.ReleaseManagement.Types";

import { css } from "VSSUI/Util";
import { IContextualMenuItem } from "OfficeFabric/ContextualMenu";
import * as DetailsListProps from "OfficeFabric/DetailsList";

import * as Utils_Array from "VSS/Utils/Array";
import * as Utils_String from "VSS/Utils/String";

import "VSS/LoaderPlugins/Css!RM:PipelineWorkflow/Scripts/SharedComponents/CreateRelease/CreateReleaseArtifactsComponent";

export interface IReleaseDialogArtifactsProps extends Store.IReleaseDialogArtifactsState, Base.IProps {
    onArtifactSelectedVersionChange(artifactIndex: number, newSelectedVersion: IArtifactVersion): void;
}

export class CreateReleaseArtifactsComponent extends Base.Component<IReleaseDialogArtifactsProps, Base.IStateless>{
    private static readonly ARTIFACT_SOURCE_HEADER_KEY: string = "alias";
    private static readonly ARTIFACT_VERSION_BRANCH_HEADER_KEY: string = "versions";
    private static readonly ARTIFACT_ERROR_HEADER_KEY: string = "error-icon";
    private static readonly ARTIFACT_VERSION_PICKER_HEADER_KEY: string = "versionPickerMenu";

    public render(): JSX.Element {
        let contentClassName: string = !!this.props ? this.props.cssClass : Utils_String.empty;
        return (
            <div className={css("create-release-artifacts-content", contentClassName)}>
                {this._artifactsDisplayContent()}
            </div>
        );
    }

    private _getSelectedVersion(artifactVersion: PipelineArtifactVersion, artifactSelectedVersion: string): string {
        let selectedVersion: string;
        if (!artifactVersion || !artifactVersion.versions || !artifactSelectedVersion) {
            selectedVersion = artifactSelectedVersion;
        } else {
            let version = Utils_Array.first(artifactVersion.versions, (version: PipelineBuildVersion): boolean => {
                return Utils_String.localeIgnoreCaseComparer(version.name, artifactSelectedVersion) === 0;
            });

            if (version) {
                selectedVersion = ArtifactUtility.getArtifactVersionDisplayValue(version);
            } else {
                selectedVersion = artifactSelectedVersion;
            }
        }

        return selectedVersion;
    }

    private _getVersions(artifactVersion: PipelineArtifactVersion): string[] {
        let versions: string[] = [];
        if (artifactVersion && artifactVersion.versions) {
            versions = artifactVersion.versions.map((version: PipelineBuildVersion): string => {
                return ArtifactUtility.getArtifactVersionDisplayValue(version);
            });
        }

        return versions;
    }

    private _getErrorTooltipComponent(hasError: boolean, errorMessage: string, index: number): JSX.Element {
        let errorClass = hasError ? "artifact-version-error" : undefined;
        let referenceId: string = `${index}-tooltip-message-override`;
        return (
            hasError &&
                <TooltipHost directionalHint={DirectionalHint.bottomLeftEdge} content={errorMessage}>
                    <FlatViewIcon ariaLiveRegionMessage={errorMessage} rowSelected={true} iconName="Error" className={errorClass} />
                </TooltipHost>
            );
    }

    private _getArtifactsRows(): IFlatViewTableRow[] {
        const artifactSourceClassName = "artifact-source";
        let artifactsRows: IFlatViewTableRow[] = [];

        let artifactsVersions: EnvironmentUtils.IArtifactVersionData[] = this.props.artifactsVersionsData || [];
        let contributions: Contribution[] = this.props.versionPickerContributions || [];
        let hasMenuItems: boolean = this._hasContributionMenuItems(artifactsVersions, contributions);

        artifactsVersions.forEach((artifact: EnvironmentUtils.IArtifactVersionData, index: number): void => {
            let row: IFlatViewTableRow = { cells: {} };
            let versions: string[] = this._getVersions(artifact.artifactVersion);
            let artifactVersionAlias: string = !!artifact.artifactVersion ? artifact.artifactVersion.alias : Utils_String.empty;

            // icon column
            row.cells[CreateReleaseArtifactsComponent.ARTIFACT_ERROR_HEADER_KEY] = this._getIconCellContent(artifact, index);

            let content = (
                <FlatViewText 
                    text={artifactVersionAlias} />
            );

            row.cells[CreateReleaseArtifactsComponent.ARTIFACT_SOURCE_HEADER_KEY] = {
                cssClass: "artifact-source-cell",
                content: content,
                contentType: ContentType.JsxElement,
                isTextDisabled: true,
            } as IFlatViewCell;

            let selectedVersionName = this._getSelectedVersion(artifact.artifactVersion, artifact.selectedVersionName);
            row.cells[CreateReleaseArtifactsComponent.ARTIFACT_VERSION_BRANCH_HEADER_KEY] = {
                content: (
                    <FlatViewDropdown
                        maxAutoExpandDropWidth={300}
                        cssClass="artifact-version-selector"
                        conditions={versions}
                        selectedCondition={selectedVersionName}
                        rowSelected={false}
                        onValueChanged={(newSelectedVersionName: string): void => {
                            if (this.props.onArtifactSelectedVersionChange) {
                                let newSelectedVersionId = ArtifactUtility.getArtifactVersionIdByName(artifact.artifactVersion.versions, newSelectedVersionName, true);
                                if (!newSelectedVersionId) {
                                    // When no match is found, assign versionName to versionId so that the not-found artifact version can be correctly fetched 
                                    // when user types buildId in the version combobox
                                    newSelectedVersionId = newSelectedVersionName;
                                }
                                let newSelectedVersion: IArtifactVersion = {
                                    id: newSelectedVersionId,
                                    name: newSelectedVersionName
                                } as IArtifactVersion;
                                this.props.onArtifactSelectedVersionChange(index, newSelectedVersion);
                            }
                        }}
                        ariaLabel={Utils_String.localeFormat(Resources.ArtifactVersionsOptionAriaLabelText, artifact.artifactVersion.alias)}
                        type={ComboBoxType.Editable}
                        enableFilter={true}
                        allowEdit={true}
                        hasErrors={artifact.hasError}
                    />
                ),
                contentType: ContentType.JsxElement,
                contentHasErrors: artifact.hasError
            } as IFlatViewCell;

            if (hasMenuItems) {
                let menuItems = this._getVersionPickerMenuItems(contributions, artifact);
                row.cells[CreateReleaseArtifactsComponent.ARTIFACT_VERSION_PICKER_HEADER_KEY] = 
                    this._getMenuItemCellContent(artifact.artifactSource, index, menuItems);
            }

            artifactsRows.push(row);
        });

        return artifactsRows;
    }

    private _getVersionPickerMenuItems(contributions: Contribution[], artifact: EnvironmentUtils.IArtifactVersionData): IContextualMenuItem[] {
        let menuContributions: Contribution[] = this._getContributionsForArtifactType(contributions, artifact.artifactSource.type);
        if (menuContributions.length === 0) {
            return [];
        }

        const menuItems = menuContributions.map(contribution => {
            return {
                key: contribution.id,
                name: contribution.properties.name || contribution.properties.text,
                href: contribution.properties.href,
                iconProps: contribution.properties.iconProps,
                onClick: (event?, item?: IContextualMenuItem) => {
                    if (item && item.data) {
                        ContributionSource.instance().getContributionResult<{ execute: (menuContext: IVersionPickerMenuItemContext) => void }>(contribution as IExtensionContribution)
                            .then(object => object.execute(item.data.menuContext));
                    }
                }
            } as IContextualMenuItem;
        });
        return menuItems;
    }

    private _getMenuItemCellContent(artifactSource: PipelineArtifactDefinition, index: number, menuItems: IContextualMenuItem[]): IFlatViewCell {
        if (!menuItems || !menuItems.length) {
            return;
        }

        const menuContext: IVersionPickerMenuItemContext = {
            artifact: artifactSource,
            onVersionSelected: (version: IArtifactVersion) => {
                if (this.props.onArtifactSelectedVersionChange) {
                    this.props.onArtifactSelectedVersionChange(index, version);
                }
            }
        };

        const clonedItems = menuItems.map(item => { return { ...item }; });
        clonedItems.forEach(item => item.data = { menuContext: menuContext });

        const content = (
            <MenuItemCellWrapper>
                <TooltipHost
                    content={Resources.VersionPickerTooltip}
                    directionalHint={DirectionalHint.topCenter}
                    hostClassName={"flex flex-grow"}
                >
                    <ContextualMenuButton
                        className={ "version-picker-value-menu" }
                        iconProps={{ iconName: "MoreVertical" }}
                        getItems={ () => clonedItems }
                    />
                </TooltipHost>
            </MenuItemCellWrapper>
        );
        return { content: content, contentType: ContentType.JsxElement };
    }

    private _getIconCellContent(artifact: EnvironmentUtils.IArtifactVersionData, index: number): IFlatViewCell {
        let content: JSX.Element = this._getErrorTooltipComponent(artifact.hasError, artifact.errorMessage, index);
        return {
            content: content,
            contentType: ContentType.JsxElement
        } as IFlatViewCell;
    }

    private _getContributionsForArtifactType(allContributions: Contribution[], artifactType: string): Contribution[] {
        let contributions: Contribution[] = [];
        if (artifactType && allContributions && allContributions.length > 0 && FeatureFlagUtils.isArtifactVersionPickerEnabled()) {
            allContributions.forEach(contribution => {
                // contribution has to match the artifact type it targets
                if (contribution.properties 
                    && contribution.properties.artifactTypes
                    && Utils_Array.contains(contribution.properties.artifactTypes, artifactType)) {
                    Utils_Array.add(contributions, contribution);
                }
            });
        }
        return contributions;
    }

    private _isVersionPickerSupportedForSourceRepositoryType(artifactVersion: PipelineArtifactVersion, artifactType: string): boolean {
        if (Utils_String.localeIgnoreCaseComparer(artifactType, ArtifactTypes.Build) !== 0) {
            // source repository type does not apply to non-Build artifact type
            return true;
        }
        // only TfsGit repository type supports version picker for Build artifact type
        return artifactVersion 
            && artifactVersion.versions 
            && artifactVersion.versions.length > 0 
            && Utils_String.localeIgnoreCaseComparer(artifactVersion.versions[0].sourceRepositoryType, CreateReleaseArtifactConstants.repoTypeTfsGit) === 0;
    }

    private _hasContributionMenuItems(artifacts: EnvironmentUtils.IArtifactVersionData[], contributions: Contribution[]): boolean {
        let hasMenuItems: boolean = false;
        if (artifacts && artifacts.length > 0) {
            artifacts.forEach((artifact: EnvironmentUtils.IArtifactVersionData) => {
                if (this._isVersionPickerSupportedForSourceRepositoryType(artifact.artifactVersion, artifact.artifactSource.type)
                    && this._getContributionsForArtifactType(contributions, artifact.artifactSource.type).length > 0) {
                    hasMenuItems = true;
                    return;
                }
            });
        }
        return hasMenuItems;
    }

    private _artifactsDisplayContent(): JSX.Element {
        let headers: IFlatViewColumn[] = [];
        let artifacts: EnvironmentUtils.IArtifactVersionData[] = this.props.artifactsVersionsData || [];
        let contributions: Contribution[] = this.props.versionPickerContributions || [];
        let hasMenuItems: boolean = this._hasContributionMenuItems(artifacts, contributions);

        // error icon
        headers.push({
            key: CreateReleaseArtifactsComponent.ARTIFACT_ERROR_HEADER_KEY,
            name: Resources.CreateReleasePanelArtifactErrorIconAriaLabel,
            isIconOnly: true,
            isFixedColumn: true,
            minWidth: 20,
            maxWidth: 20,
            columnActionsMode: DetailsListProps.ColumnActionsMode.disabled
        });

        headers.push({
            key: CreateReleaseArtifactsComponent.ARTIFACT_SOURCE_HEADER_KEY,
            name: Resources.ArtifactSourceAlias,
            isFixedColumn: true,
            maxWidth: 217,
            minWidth: 217,
            columnActionsMode: DetailsListProps.ColumnActionsMode.disabled
        });

        headers.push({
            key: CreateReleaseArtifactsComponent.ARTIFACT_VERSION_BRANCH_HEADER_KEY,
            name: Resources.VersionBranchCombinationText,
            isFixedColumn: true,
            maxWidth: hasMenuItems ? 483 : 493,
            minWidth: hasMenuItems ? 270 : 280,
            columnActionsMode: DetailsListProps.ColumnActionsMode.disabled
        });

        if (hasMenuItems) {
            headers.push({
                key: CreateReleaseArtifactsComponent.ARTIFACT_VERSION_PICKER_HEADER_KEY,
                name: Utils_String.empty,
                isFixedColumn: false,
                maxWidth: 10,
                minWidth: 10,
                columnActionsMode: DetailsListProps.ColumnActionsMode.disabled
            });
        }

        return (
            <FlatViewTable
                headers={headers}
                rows={this._getArtifactsRows()}
                onCellValueChanged={(value: string, newCellIndex: ICellIndex) => { }} />
        );
    }
}

class MenuItemCellWrapper<T extends { rowSelected?: boolean } & Base.IProps  = { rowSelected: boolean }> extends Base.Component<T, {}> {
    public render(): JSX.Element {
        return (
            <div className={css("flex flex-grow", this.props.rowSelected ? "row-selected" : "")}>
                {this.props.children}
            </div>
        );
    }
}
